import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { AdminPurchaseOrdersFacade } from './admin-purchase-orders.facade';
import {
  CreateAndSendPurchaseOrderDto,
  PurchaseOrder,
  PurchaseOrderContractData,
  UpsertPurchaseOrderDto,
} from '@models/resources/purchase-order.model';

/**
 * Dummy-Variante der Staff-Kaufauftrag-Actions (Sprint 9, ADR-011).
 *
 * Hält pro Order-ID einen In-Memory-Zustand und bildet die
 * Zustandsübergänge grob nach, damit die Admin-Card im dummy-Modus
 * (`?dataSource=dummy`) klickbar bleibt. Der reale Workflow (Provider,
 * Mails, Gates) läuft in den Playwright-Critical-Flows gegen HTTP.
 */
@Injectable()
export class DummyAdminPurchaseOrdersFacade implements AdminPurchaseOrdersFacade {
  private readonly store = new Map<number, PurchaseOrder>();
  private nextId = 9000;

  /** Im Dummy-Modus gibt es keine Vorgänger-Aufträge → Stammdaten-Prefill. */
  lastContract(_customerId: number): Observable<PurchaseOrderContractData | null> {
    return of(null).pipe(delay(60));
  }

  createAndSend(txId: number, dto: CreateAndSendPurchaseOrderDto): Observable<PurchaseOrder> {
    const now = new Date().toISOString();
    const order: PurchaseOrder = {
      id: this.nextId++,
      transaction_request_id: txId,
      metal: { id: 1, symbol: dto.metal_symbol, name: dto.metal_symbol },
      amount_eur: dto.amount_eur,
      status: 'sent',
      identity_method: null,
      revision: 1,
      current_step: 'identity_choice',
      sent_at: now,
      signed_at: null,
      confirmed_at: null,
      returned_at: null,
      return_comment: null,
      signature_provider: null,
      ident_redirect_url: null,
      signature_url: null,
      sign_token: null,
      documents: [],
      created_at: now,
      updated_at: now,
    };
    this.store.set(order.id, order);
    return of(order).pipe(delay(120));
  }

  create(txId: number, dto: UpsertPurchaseOrderDto): Observable<PurchaseOrder> {
    const now = new Date().toISOString();
    const order: PurchaseOrder = {
      id: this.nextId++,
      transaction_request_id: txId,
      metal: { id: dto.metal_id ?? 1, symbol: 'Au', name: 'Gold' },
      amount_eur: dto.amount_eur ?? 10000,
      status: 'draft',
      identity_method: null,
      revision: 1,
      current_step: 'draft',
      sent_at: null,
      signed_at: null,
      confirmed_at: null,
      returned_at: null,
      return_comment: null,
      signature_provider: null,
      ident_redirect_url: null,
      signature_url: null,
      sign_token: null,
      documents: [],
      created_at: now,
      updated_at: now,
    };
    this.store.set(order.id, order);
    return of(order).pipe(delay(120));
  }

  get(orderId: number): Observable<PurchaseOrder> {
    return of(this.require(orderId)).pipe(delay(80));
  }

  update(orderId: number, dto: UpsertPurchaseOrderDto): Observable<PurchaseOrder> {
    const order = this.require(orderId);
    const updated: PurchaseOrder = {
      ...order,
      amount_eur: dto.amount_eur ?? order.amount_eur,
      metal: dto.metal_id ? { ...order.metal, id: dto.metal_id } : order.metal,
      revision: order.status === 'sent' ? order.revision + 1 : order.revision,
    };
    this.store.set(orderId, updated);
    return of(updated).pipe(delay(120));
  }

  send(orderId: number): Observable<PurchaseOrder> {
    return this.patch(orderId, {
      status: 'sent',
      sent_at: new Date().toISOString(),
      current_step: 'identity_choice',
    });
  }

  returnToCustomer(orderId: number, comment: string): Observable<PurchaseOrder> {
    const order = this.require(orderId);
    return this.patch(orderId, {
      status: 'sent',
      returned_at: new Date().toISOString(),
      return_comment: comment,
      revision: order.revision + 1,
      signed_at: null,
      current_step: 'identity_choice',
    });
  }

  confirmIdentity(orderId: number): Observable<PurchaseOrder> {
    return this.patch(orderId, { current_step: 'signature' });
  }

  private patch(orderId: number, changes: Partial<PurchaseOrder>): Observable<PurchaseOrder> {
    const order = this.require(orderId);
    const updated: PurchaseOrder = { ...order, ...changes, updated_at: new Date().toISOString() };
    this.store.set(orderId, updated);
    return of(updated).pipe(delay(120));
  }

  private require(orderId: number): PurchaseOrder {
    const order = this.store.get(orderId);
    if (!order) {
      throw new Error(`Dummy purchase order ${orderId} not found`);
    }
    return order;
  }
}
